/**
 * Number / amount formatters.
 *
 * Cents are stored as int. For JPY we treat "1 cent = 1 yen" (the currency
 * has no minor units); for USD / EUR / GBP etc, "1 cent = 0.01 unit". The
 * helpers below hide that distinction from view code.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'JPY', 'KRW', 'VND', 'IDR', 'CLP', 'PYG', 'UGX', 'XAF', 'XOF',
]);

export function isZeroDecimal(currency: string): boolean {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase());
}

export function centsToMajor(cents: number | string, currency: string): number {
  const n = typeof cents === 'string' ? Number(cents) : cents;
  return isZeroDecimal(currency) ? n : n / 100;
}

export function formatAmount(cents: number | string, currency: string, locale = 'ja-JP'): string {
  const major = centsToMajor(cents, currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: isZeroDecimal(currency) ? 0 : 2,
  }).format(major);
}

export function formatDate(date: string | Date | null | undefined, locale = 'ja-JP'): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

export function relativeTime(date: string | Date, locale = 'ja-JP'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = d.getTime() - Date.now();
  const minutes = Math.round(diffMs / 60000);
  const hours = Math.round(diffMs / 3_600_000);
  const days = Math.round(diffMs / 86_400_000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(days, 'day');
}

/** Half-width digits in JP locale would otherwise look out of place against names. */
export function toFullWidthDigits(input: string): string {
  return input.replace(/\d/g, (d) => String.fromCharCode(d.charCodeAt(0) - 48 + 0xFF10));
}

export function formatKanjiUnit(yen: number): string {
  if (Math.abs(yen) >= 100_000_000) return `${(yen / 100_000_000).toFixed(1)}億円`;
  if (Math.abs(yen) >= 10_000) return `${(yen / 10_000).toFixed(0)}万円`;
  return `${yen.toLocaleString('ja-JP')}円`;
}
